import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const stackVariants = cva(
  'flex',
  {
    variants: {
      direction: {
        row: 'flex-row',
        column: 'flex-col',
        'row-reverse': 'flex-row-reverse',
        'column-reverse': 'flex-col-reverse',
      },
      align: {
        start: 'items-start',
        center: 'items-center',
        end: 'items-end',
        stretch: 'items-stretch',
        baseline: 'items-baseline',
      },
      justify: {
        start: 'justify-start',
        center: 'justify-center',
        end: 'justify-end',
        between: 'justify-between',
        around: 'justify-around',
        evenly: 'justify-evenly',
      },
      wrap: {
        nowrap: 'flex-nowrap',
        wrap: 'flex-wrap',
        'wrap-reverse': 'flex-wrap-reverse',
      },
      gap: {
        0: 'gap-0',
        1: 'gap-1',
        2: 'gap-2',
        3: 'gap-3',
        4: 'gap-4',
        5: 'gap-5',
        6: 'gap-6',
        8: 'gap-8',
        10: 'gap-10',
        12: 'gap-12',
        16: 'gap-16',
      },
    },
    defaultVariants: {
      direction: 'column',
      align: 'stretch',
      justify: 'start',
      wrap: 'nowrap',
      gap: 4,
    },
  }
);

export interface StackProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof stackVariants> {
  as?: React.ElementType;
  // Alias used in demo examples
  spacing?: number;
}

export function Stack({
  className,
  direction,
  align,
  justify,
  wrap,
  gap,
  spacing,
  as: Component = 'div',
  ...props
}: StackProps) {
  return (
    <Component
      className={cn(
        stackVariants({ direction, align, justify, wrap, gap: (spacing as any) ?? gap }),
        className
      )}
      {...props}
    />
  );
}

// Convenience components
export interface VStackProps extends Omit<StackProps, 'direction'> {}
export function VStack(props: VStackProps) {
  return <Stack direction="column" {...props} />;
}

export interface HStackProps extends Omit<StackProps, 'direction'> {}
export function HStack(props: HStackProps) {
  return <Stack direction="row" {...props} />;
}
